"use client";

import { useRef, useCallback } from "react";
import api from "@/lib/api/client";
import { useLanguage } from "@/lib/language-context";
import { useSettings } from "@/lib/settings-context";

interface PrefetchedVocab {
  words: Map<string, {
    word: string;
    baseForm: string;
    translation: string;
    pronunciation?: string;
    vocalizationTip?: string;
  }>;
  expressions: Array<{
    expression: string;
    translation: string;
    literal?: string;
    components: Array<{ word: string; meaning: string }>;
  }>;
  phoneticTranscription?: string;
}

interface PrefetchCache {
  [key: string]: {
    promise: Promise<PrefetchedVocab>;
    result?: PrefetchedVocab;
  };
}

/**
 * Hook for pre-fetching vocab when user shows interest in a node.
 * Call prefetch() when node is tapped, get() when modal opens.
 */
export function useVocabPrefetch() {
  const { targetLanguage, nativeLanguage } = useLanguage();
  const { userProfile } = useSettings();
  const cacheRef = useRef<PrefetchCache>({});

  const prefetch = useCallback(
    async (nodeText: string, alternatives?: Array<{ text: string }>) => {
      // Build cache key from phrase texts + known languages (tips depend on known langs)
      const allTexts = [nodeText, ...(alternatives?.map((a) => a.text) || [])];
      const knownLangsKey = (userProfile.knownLanguages || []).sort().join(",");
      const cacheKey = `${allTexts.join("|")}|${knownLangsKey}`;

      // Already fetching or fetched
      if (cacheRef.current[cacheKey]) {
        return cacheRef.current[cacheKey].promise;
      }

      // Extract all unique words from all phrases
      const allWords = new Set<string>();
      for (const text of allTexts) {
        const words = text
          .split(/\s+/)
          .map((w) => w.replace(/[.,!?;:'"()¿¡«»„"]/g, "").trim())
          .filter((w) => w.length >= 1);
        for (const word of words) {
          allWords.add(word);
        }
      }

      const wordsArray = Array.from(allWords);

      // Start fetch
      const promise = (async (): Promise<PrefetchedVocab> => {
        const wordMap = new Map<string, PrefetchedVocab["words"] extends Map<string, infer V> ? V : never>();
        const expressions: PrefetchedVocab["expressions"] = [];

        try {
          // Filter out native language from known languages (it's implied)
          const nativeCode = nativeLanguage.code.split("-")[0]!.toLowerCase();
          const knownLangs = userProfile.knownLanguages.filter(
            (code) => code.toLowerCase() !== nativeCode
          );
          const { data } = await api.POST("/api/vocab-lookup", {
            body: {
              words: wordsArray,
              sentence: nodeText, // Use main phrase for expression extraction
              targetLanguage: targetLanguage.code,
              nativeLanguage: nativeLanguage.code,
              knownLanguages: knownLangs,
            },
          });

          if (data?.words) {
            data.words.forEach((entry, idx) => {
              const originalWord = wordsArray[idx]?.toLowerCase();
              if (originalWord) {
                wordMap.set(originalWord, {
                  word: wordsArray[idx]!,
                  baseForm: entry.baseForm,
                  translation: entry.translation,
                  pronunciation: entry.pronunciation || undefined,
                  vocalizationTip: entry.vocalizationTip || undefined,
                });
              }
            });
          }

          if (data?.expressions) {
            for (const expr of data.expressions) {
              expressions.push({
                expression: expr.expression,
                translation: expr.translation,
                literal: expr.literal || undefined,
                components:
                  expr.components?.map((c) => ({
                    word: c.word,
                    meaning: c.meaning,
                  })) || [],
              });
            }
          }

          // Capture phonetic transcription
          const phoneticTranscription = data?.phoneticTranscription || undefined;

          const result = { words: wordMap, expressions, phoneticTranscription };
          // Store result for instant access
          if (cacheRef.current[cacheKey]) {
            cacheRef.current[cacheKey].result = result;
          }
          return result;
        } catch (err) {
          console.error("Prefetch vocab failed:", err);
          return { words: wordMap, expressions, phoneticTranscription: undefined };
        }
      })();

      cacheRef.current[cacheKey] = { promise };
      return promise;
    },
    [targetLanguage.code, nativeLanguage.code, userProfile.knownLanguages]
  );

  const get = useCallback(
    (nodeText: string, alternatives?: Array<{ text: string }>) => {
      const allTexts = [nodeText, ...(alternatives?.map((a) => a.text) || [])];
      const knownLangsKey = (userProfile.knownLanguages || []).sort().join(",");
      const cacheKey = `${allTexts.join("|")}|${knownLangsKey}`;
      return cacheRef.current[cacheKey]?.result;
    },
    [userProfile.knownLanguages]
  );

  const getPromise = useCallback(
    (nodeText: string, alternatives?: Array<{ text: string }>) => {
      const allTexts = [nodeText, ...(alternatives?.map((a) => a.text) || [])];
      const knownLangsKey = (userProfile.knownLanguages || []).sort().join(",");
      const cacheKey = `${allTexts.join("|")}|${knownLangsKey}`;
      return cacheRef.current[cacheKey]?.promise;
    },
    [userProfile.knownLanguages]
  );

  return { prefetch, get, getPromise };
}
